import type { TFunction } from 'i18next';
import {
  parseUnitPriceInput,
  positivePriceMessageParams,
} from '~/utils/moneyBounds';
import { Building2, Truck, GripVertical } from 'lucide-react';
import { RemoveSellerConfirmModal } from './RemoveSellerConfirmModal';
import { RemoveVendorButton } from './RemoveVendorButton';
import { FacilityPicker } from './FacilityPicker';
import {
  ComparisonTableHeadRow,
  TableHeader,
  nextSortDirection,
  type ClickSortDirection,
  type TableHeaderDefinition,
} from './SellerComparisonTableHeader';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
  type ReactNode,
} from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import type {
  Currency,
  PlatformProduct,
  Quantity,
  UnitCode,
} from '~/graphql/graphql';
import { buildComparisonInventoryFilters } from '~/pages/order-builder/comparisonInventoryFilters';
import {
  CANNED_TALLY_SORTABLE_KEYS,
  VENDOR_GROUP_SORTABLE_KEYS,
} from '~/pages/order-builder/comparisonSort';
import {
  formatLengthWithBasis,
  sellerLabel,
  formatUnitPrice,
  isWithinTarget,
  priceUnitLabel,
} from '~/utils';
import {
  roundUnitPrice,
  unitPriceMaxDecimalsForInput,
} from '~/utils/unitPrice';
import {
  applyColumnOrder,
  getColumnDragAccessibility,
  useColumnDragReorder,
  useColumnOrderPreference,
} from '~/components/ui/table';
import {
  useGetUserPreferences,
  useUpdateUserPreferences,
  vendorGroupCartKey,
} from '~/api';
import { useGetPlatformProductCannedTalliesByOrganizationIds } from '~/api/canned-tally';
import type { PlatformProductCannedTalliesByOrganizationIdsQuery } from '~/graphql/graphql';
import type {
  SellerInventoryForBuyer,
  SellerVendorInventoryGroupForBuyer,
} from '~/types/inventory';
import type { SellerProductInventoriesForBuyerFilters } from '~/api/inventory';
import { AnimatedPrice } from './AnimatedPrice';
import {
  useRowReorderAnimation,
  PRICE_FLASH_STYLES,
} from './sellerComparisonAnimations';
import type { RouteSort } from '~/hooks/usePaginatedQuery';
import { PriceEditControl } from '~/components/common/PriceEditControl';
import { lowestPrice, type Price } from '~/utils/money';
import {
  CannedTallySellerGroupRow,
  type CannedTallyGroupData,
} from './CannedTallySellerGroupRow';
import {
  negotiationKey,
  type NegotiationSelection,
  type NegotiationSelectionTarget,
} from './negotiationSelection';
import {
  NegotiationCheckboxCell,
  NegotiationSellerCell,
} from './NegotiationCheckboxCell';
import {
  negotiationCellState,
  type NegotiationCellState,
} from './negotiationCellState';

export type { CannedTallyGroupData };

export type CannedTallyOption =
  PlatformProductCannedTalliesByOrganizationIdsQuery['platformProductCannedTalliesByOrganizationIds'][number]['tallies'][number];

interface TableCellProps {
  type?: 'seller' | 'price' | 'stock' | 'leadTime' | 'actions' | 'simple';
  bgColor?: 'success' | 'error' | 'default';
  className?: string;
  icon?: React.ComponentType<{ className?: string }>;
  primary?: string;
  secondary?: string;
  badge?: string;
  isItalic?: boolean;
  children?: React.ReactNode;
  stickTo?: 'left' | 'right' | 'none';
}

const NOOP = () => {};
const EMPTY_NEGOTIATION_KEYS = new Set<string>();
const NO_NEGOTIATION_SELECTION: NegotiationSelection = {
  selectedKeys: EMPTY_NEGOTIATION_KEYS,
  currency: null,
  onToggle: NOOP,
};

function sortAscendingByNullablePrice<T>(
  items: T[],
  getPrice: (item: T) => number | null
): T[] {
  return [...items].sort((a, b) => {
    const priceA = getPrice(a);
    const priceB = getPrice(b);
    if (priceA == null && priceB == null) return 0;
    if (priceA == null) return 1;
    if (priceB == null) return -1;
    return priceA - priceB;
  });
}

type SellerComparisonTableProps = {
  targetPrice: number;
  cartIdentifiers: string[];
  selectedLocationId: string | undefined | null;
  onAddToCart: (
    platformProductLengthId: string,
    stock: number,
    minimumOrder: Quantity | null,
    facilityId: string,
    suggestedPrice: number | undefined,
    vendorGroupId: string
  ) => void;
  onPriceEdit: (vendorGroupId: string, newPrice: number) => void;
  onSuggestTallyPrice: (target: NegotiationSelectionTarget) => void;
  onAddTallyToCart?: (
    tallyId: string,
    tallyCount: number,
    facilityId: string,
    suggestedPrice: number | undefined,
    sellingOrganizationId: string
  ) => void;
  onTallyPriceEdit?: (tallyId: string, orgId: string, newPrice: number) => void;
  editedTallyPriceByKey?: Map<string, number>;
  editedVendorGroupPriceByKey?: Map<string, number>;
  pendingTallyNegotiationKeys: Set<string>;
  pendingNegotiationKeys?: Set<string>;
  negotiationSelection?: NegotiationSelection;
  acceptedTallyPriceByKey?: Map<
    string,
    { amount: number; currency: Currency; unit: UnitCode }
  >;
  isCannedTally?: boolean;
  platformProduct?: PlatformProduct;
  priceUnit?: UnitCode;
  includeInventoryLengths?: boolean;
  platformProductId?: string;
  platformProductLengthIds?: string[];
  transportVehicleId?: string | null;
  selectedTallyName?: string;
  vendorGroups?: SellerVendorInventoryGroupForBuyer[];
  isLoadingMoreVendorGroups?: boolean;
  hasMoreVendorGroups?: boolean | null;
  cannedTallyGroups?: CannedTallyGroupData[];
  isLoadingMoreCannedTallyGroups?: boolean;
  hasMoreCannedTallyGroups?: boolean | null;
  cannedTallyGroupsSentinelRef?: React.Ref<HTMLDivElement>;
  vendorGroupsSentinelRef?: React.Ref<HTMLDivElement>;
  sort?: RouteSort;
  onSort?: (field: string, direction: 'asc' | 'desc' | null) => void;
  onSellerRemoved?: (organizationId: string) => void;
};

const TableCell = ({
  children,
  bgColor = 'default',
  className = '',
  stickTo = 'none',
}: Readonly<TableCellProps>) => {
  const getBgClass = () => {
    switch (bgColor) {
      case 'success':
        return 'bg-success-50';
      case 'error':
        return 'bg-error-100';
      default:
        return '';
    }
  };

  return (
    <td
      className={`px-6 py-4 whitespace-nowrap ${getBgClass()} ${className} ${stickTo === 'right' ? 'sticky right-0 bg-neut-light z-[1]' : stickTo === 'left' ? 'sticky left-0 bg-neut-light z-[1]' : ''}`}
    >
      {children}
    </td>
  );
};

const getTableHeaders = (t: TFunction): TableHeaderDefinition[] => [
  {
    key: 'seller',
    label: t('order_builder.inventory.seller'),
    showTrending: false,
  },
  {
    key: 'length',
    label: t('order_builder.inventory.length'),
    showTrending: false,
  },
  {
    key: 'price',
    label: t('order_builder.inventory.price'),
    showTrending: true,
  },
  {
    key: 'stock',
    label: t('order_builder.inventory.stock'),
    showTrending: false,
  },
  {
    key: 'facility',
    label: t('order_builder.inventory.facility'),
    showTrending: false,
  },
  {
    key: 'leadTime',
    label: t('order_builder.inventory.lead_time'),
    showTrending: false,
  },
  {
    key: 'actions',
    label: t('order_builder.inventory.actions'),
    showTrending: false,
    clickSortable: false,
    stickTo: 'right',
  },
];

// Canned-tally groups span facilities, so there's no single facility column.
const getCannedTallyTableHeaders = (t: TFunction): TableHeaderDefinition[] =>
  getTableHeaders(t).filter((header) => header.key !== 'facility');

// One row per (seller, length) already collapses facility out, and AC2 removes stock.
const getVendorGroupTableHeaders = (t: TFunction): TableHeaderDefinition[] =>
  getTableHeaders(t).filter(
    (header) => header.key !== 'facility' && header.key !== 'stock'
  );

function withClickSortable(
  headers: TableHeaderDefinition[],
  sortableKeys: ReadonlySet<string>
): TableHeaderDefinition[] {
  return headers.map((header) => ({
    ...header,
    clickSortable: sortableKeys.has(header.key),
  }));
}

interface VendorGroupRowData {
  id: string;
  negotiationTarget: NegotiationSelectionTarget;
  organizationId: string;
  organizationLabel: string;
  platformProductLengthId: string;
  lengthLabel: string;
  inventoryOnly: boolean;
  hasBestPrice: boolean;
  priceLabel: string;
  rawPrice: number | null;
  currencyCode: Currency;
  isWithinSetPrice: boolean;
  isPriceEdited: boolean;
  hasListedPrice: boolean;
  isSingleValuePrice: boolean;
  leadTimeLabel: string;
  facilityCount: number;
  hasPricedFacility: boolean;
}

function priceRangeLabel(
  min: { amount: number; currency: string } | null | undefined,
  max: { amount: number; currency: string } | null | undefined,
  t: TFunction
): string {
  if (!min || !max) return t('order_builder.table.no_listed_price');
  return min.amount === max.amount
    ? formatUnitPrice(min.amount, min.currency)
    : `${formatUnitPrice(min.amount, min.currency)} – ${formatUnitPrice(max.amount, max.currency)}`;
}

function getVendorGroupRowData(
  vendorGroups: SellerVendorInventoryGroupForBuyer[] | null | undefined,
  editedPriceByKey: ReadonlyMap<string, number>,
  targetPrice: number,
  t: TFunction
): VendorGroupRowData[] {
  if (!vendorGroups) return [];
  return vendorGroups.map((group) => {
    const min = group.priceRange?.min;
    const max = group.priceRange?.max;
    const currencyCode =
      min?.currency ?? max?.currency ?? group.organization.defaultCurrency;
    const editedPrice = editedPriceByKey.get(group.id);
    const rawPrice = editedPrice ?? min?.amount ?? null;
    const hasListedPrice = rawPrice != null;
    return {
      id: group.id,
      negotiationTarget: {
        kind: 'length',
        negotiationKey: negotiationKey(
          group.platformProductsLength.id,
          group.organization.id
        ),
        platformProductLengthId: group.platformProductsLength.id,
        sellingOrganizationId: group.organization.id,
        organizationName: group.organization.name,
        sellingOrganizationCode: group.organization.sellingOrganizationCode,
        currencyCode,
      },
      organizationId: group.organization.id,
      organizationLabel: sellerLabel(group.organization),
      platformProductLengthId: group.platformProductsLength.id,
      lengthLabel: formatLengthWithBasis(
        group.platformProductsLength.lengthDisplayValue,
        group.platformProductsLength.isNominal
      ),
      inventoryOnly: group.platformProductsLength.inventoryOnly ?? false,
      hasBestPrice: false,
      priceLabel:
        editedPrice != null
          ? formatUnitPrice(editedPrice, currencyCode)
          : priceRangeLabel(min, max, t),
      rawPrice,
      currencyCode,
      isWithinSetPrice: hasListedPrice && isWithinTarget(rawPrice, targetPrice),
      isPriceEdited: editedPrice != null,
      hasListedPrice,
      isSingleValuePrice:
        editedPrice != null ||
        (min != null && max != null && min.amount === max.amount),
      leadTimeLabel:
        group.minReadyToShipDays != null
          ? `${group.minReadyToShipDays} ${t('order_builder.days')}`
          : 'N/A',
      facilityCount: group.facilityCount,
      hasPricedFacility: group.hasPricedFacility,
    };
  });
}

function computeOverallMinPrice(rows: VendorGroupRowData[]): Price | null {
  return lowestPrice(
    rows.map((row) =>
      row.rawPrice == null
        ? null
        : { amount: row.rawPrice, currency: row.currencyCode }
    )
  );
}

interface VendorGroupActionsCellProps {
  row: VendorGroupRowData;
  facilityPickerBaseFilter: Pick<
    SellerProductInventoriesForBuyerFilters,
    'destinationLocationId' | 'deliveryMode' | 'includeInventoryLengths'
  >;
  selectedLocationId: string | undefined | null;
  alreadyInCart: boolean;
  accessibleLabel: string;
  onAddToCart: SellerComparisonTableProps['onAddToCart'];
  onRemoveVendor: (organizationId: string, organizationName: string) => void;
  t: TFunction;
}

// Extracted so onSelectFacility gets a stable identity via useCallback scoped to this row —
// inline in the parent's .map, a fresh arrow every render would re-run FacilityPicker's
// auto-select effect on every render (see FacilityPicker's own comment on that effect).
function VendorGroupActionsCell({
  row,
  facilityPickerBaseFilter,
  selectedLocationId,
  alreadyInCart,
  accessibleLabel,
  onAddToCart,
  onRemoveVendor,
  t,
}: Readonly<VendorGroupActionsCellProps>) {
  const canAddToCart = row.hasListedPrice || row.hasPricedFacility;

  const handleSelectFacility = useCallback(
    (inventory: SellerInventoryForBuyer, suggestedPrice: number | undefined) =>
      onAddToCart(
        row.platformProductLengthId,
        inventory.quantity?.value ?? 0,
        inventory.sellerProductConfiguration?.minimumOrderQuantity ?? null,
        inventory.facility.id,
        suggestedPrice,
        row.id
      ),
    [onAddToCart, row.platformProductLengthId, row.id]
  );

  return (
    <TableCell stickTo="right">
      <div className="flex items-center gap-2">
        <FacilityPicker
          organizationId={row.organizationId}
          platformProductLengthId={row.platformProductLengthId}
          facilityCount={row.facilityCount}
          baseFilter={facilityPickerBaseFilter}
          disabled={!selectedLocationId || !canAddToCart || alreadyInCart}
          accessibleLabel={accessibleLabel}
          suggestedPrice={
            row.isPriceEdited ? (row.rawPrice ?? undefined) : undefined
          }
          onSelectFacility={handleSelectFacility}
        />
        <RemoveVendorButton
          organizationId={row.organizationId}
          organizationName={row.organizationLabel}
          onClick={() =>
            onRemoveVendor(row.organizationId, row.organizationLabel)
          }
          t={t}
        />
      </div>
    </TableCell>
  );
}

function deriveClickSortState(activeSort?: {
  field: string;
  direction: 'ASC' | 'DESC';
}): {
  activeSortKey: string | null;
  activeSortDirection: ClickSortDirection;
  isDefaultPriceOrder: boolean;
} {
  const activeSortKey = activeSort?.field ?? null;
  const activeSortDirection: ClickSortDirection = activeSort
    ? (activeSort.direction.toLowerCase() as 'asc' | 'desc')
    : null;
  const isDefaultPriceOrder =
    !activeSort || (activeSortKey === 'price' && activeSortDirection === 'asc');
  return { activeSortKey, activeSortDirection, isDefaultPriceOrder };
}

export function SellerComparisonTable({
  targetPrice,
  cartIdentifiers,
  selectedLocationId,
  onAddToCart,
  onAddTallyToCart,
  onTallyPriceEdit,
  editedTallyPriceByKey,
  editedVendorGroupPriceByKey,
  onSuggestTallyPrice,
  onPriceEdit,
  pendingTallyNegotiationKeys,
  pendingNegotiationKeys = EMPTY_NEGOTIATION_KEYS,
  negotiationSelection = NO_NEGOTIATION_SELECTION,
  acceptedTallyPriceByKey,
  isCannedTally = false,
  platformProduct,
  priceUnit,
  includeInventoryLengths = false,
  platformProductId,
  platformProductLengthIds,
  transportVehicleId,
  selectedTallyName,
  vendorGroups,
  isLoadingMoreVendorGroups,
  hasMoreVendorGroups,
  vendorGroupsSentinelRef,
  cannedTallyGroups,
  isLoadingMoreCannedTallyGroups,
  hasMoreCannedTallyGroups,
  cannedTallyGroupsSentinelRef,
  sort,
  onSort,
  onSellerRemoved,
}: Readonly<SellerComparisonTableProps>) {
  const { t } = useTranslation(['order-builder', 'general']);

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [removeSellerTarget, setRemoveSellerTarget] = useState<{
    organizationId: string;
    organizationName: string;
  } | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLDivElement>(null);

  const {
    activeSortKey: activeVendorSortKey,
    activeSortDirection: activeVendorSortDirection,
    isDefaultPriceOrder: isDefaultVendorPriceOrder,
  } = deriveClickSortState(sort?.[0]);

  function handleVendorHeaderSortClick(columnKey: string) {
    onSort?.(
      columnKey,
      nextSortDirection(
        activeVendorSortKey,
        activeVendorSortDirection,
        columnKey
      )
    );
  }

  const handlePriceEditClick = (rowId: string) => {
    setEditingRowId(rowId);
  };

  const handlePriceEditCancel = () => {
    setEditingRowId(null);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    function update() {
      if (!container) return;
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const canRight = Math.ceil(scrollLeft) < maxScroll;

      if (shadowRef.current) {
        const lastHeader = container.querySelector(
          'th:last-child'
        ) as HTMLElement | null;
        const stickyColumnWidth = lastHeader?.offsetWidth ?? 0;
        shadowRef.current.style.left = `${scrollLeft + container.clientWidth - stickyColumnWidth - 16}px`;
        shadowRef.current.style.opacity = canRight ? '1' : '0';
        shadowRef.current.classList.toggle('sticky-col-glow', canRight);
      }
    }

    update();
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      container.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [vendorGroups]);

  const baseVendorGroupRows = useMemo(
    () =>
      getVendorGroupRowData(
        vendorGroups,
        editedVendorGroupPriceByKey ?? new Map(),
        targetPrice,
        t
      ),
    [vendorGroups, editedVendorGroupPriceByKey, targetPrice, t]
  );

  const overallMinPrice = useMemo(
    () => computeOverallMinPrice(baseVendorGroupRows),
    [baseVendorGroupRows]
  );

  const vendorGroupRows = useMemo(() => {
    const rows = isDefaultVendorPriceOrder
      ? baseVendorGroupRows.map((row) => ({
          ...row,
          hasBestPrice:
            overallMinPrice != null &&
            row.currencyCode === overallMinPrice.currency &&
            row.rawPrice === overallMinPrice.amount,
        }))
      : baseVendorGroupRows;
    return isDefaultVendorPriceOrder
      ? sortAscendingByNullablePrice(rows, (row) => row.rawPrice)
      : rows;
  }, [baseVendorGroupRows, overallMinPrice, isDefaultVendorPriceOrder]);

  const handlePriceEditSubmit = (value: string, row: VendorGroupRowData) => {
    const { amount } = parseUnitPriceInput(value);
    if (amount !== undefined) {
      const isUnchanged =
        row.rawPrice != null && amount === roundUnitPrice(row.rawPrice);
      if (!isUnchanged) onPriceEdit(row.id, amount);
      setEditingRowId(null);
    } else {
      toast.error(
        t('order_builder.table.invalid_edit_price', {
          ...positivePriceMessageParams,
          decimals: unitPriceMaxDecimalsForInput(value, row.rawPrice),
        })
      );
    }
  };

  const { data: preferencesData } = useGetUserPreferences();
  const { mutate: updatePreferences } = useUpdateUserPreferences();
  const savedColumnOrder = isCannedTally
    ? preferencesData?.userPreferences?.pricesAndAvailabilityGroupedColumnOrder
    : preferencesData?.userPreferences?.pricesAndAvailabilityColumnOrder;
  const { order, handleColumnReorderEnd } = useColumnOrderPreference(
    savedColumnOrder,
    (newOrder, { onError, onSuccess }) =>
      updatePreferences(
        {
          preferences: isCannedTally
            ? { pricesAndAvailabilityGroupedColumnOrder: newOrder }
            : { pricesAndAvailabilityColumnOrder: newOrder },
        },
        {
          onSuccess,
          onError: () => {
            onError();
            toast.error(t('order_builder.table.reorder_save_error'));
          },
        }
      )
  );
  const activeHeaders = useMemo(
    () =>
      applyColumnOrder(
        isCannedTally
          ? getCannedTallyTableHeaders(t)
          : getVendorGroupTableHeaders(t),
        order
      ),
    [t, isCannedTally, order]
  );
  const unpinnedColumnKeys = activeHeaders
    .filter((header) => header.stickTo !== 'right')
    .map((header) => header.key);
  const { sensors, activeDragColumnKey, handleDragStart, handleDragEnd } =
    useColumnDragReorder(unpinnedColumnKeys, handleColumnReorderEnd);
  const activeDragColumn = activeDragColumnKey
    ? activeHeaders.find((header) => header.key === activeDragColumnKey)
    : undefined;

  const vendorHeaderDefinitions = useMemo(
    () => withClickSortable(activeHeaders, VENDOR_GROUP_SORTABLE_KEYS),
    [activeHeaders]
  );

  const cannedTallyOrganizationIds = useMemo(
    () =>
      Array.from(
        new Set(
          (cannedTallyGroups ?? []).map(
            (group) => group.facility.organization.id
          )
        )
      ).sort(),
    [cannedTallyGroups]
  );

  const { data: cannedTalliesByOrgData } =
    useGetPlatformProductCannedTalliesByOrganizationIds(
      platformProductId,
      transportVehicleId ?? undefined,
      cannedTallyOrganizationIds,
      { enabled: isCannedTally && cannedTallyOrganizationIds.length > 0 }
    );

  const talliesByOrgId = useMemo(() => {
    const map = new Map<string, CannedTallyOption[]>();
    (
      cannedTalliesByOrgData?.platformProductCannedTalliesByOrganizationIds ??
      []
    ).forEach((result) => {
      map.set(result.organizationId, result.tallies);
    });
    return map;
  }, [cannedTalliesByOrgData]);

  const rowOrderKey = isCannedTally
    ? (cannedTallyGroups ?? []).map((group) => group.id).join(',')
    : vendorGroupRows.map((row) => row.id).join(',');
  const registerRow = useRowReorderAnimation(rowOrderKey);

  const cartIdentifierSet = useMemo(
    () => new Set(cartIdentifiers),
    [cartIdentifiers]
  );

  const facilityPickerBaseFilter = useMemo(
    () =>
      buildComparisonInventoryFilters({
        destinationLocationId: selectedLocationId ?? undefined,
        includeInventoryLengths,
      }),
    [selectedLocationId, includeInventoryLengths]
  );

  function renderVendorGroupCell(
    row: VendorGroupRowData,
    key: string,
    negotiationState: NegotiationCellState
  ): ReactNode {
    switch (key) {
      case 'seller':
        return (
          <NegotiationSellerCell key={key} state={negotiationState}>
            <Building2
              className="w-4 h-4 shrink-0 text-neut-400"
              aria-hidden="true"
            />
            <span className="block">
              <span className="block text-sm font-medium text-neut-900">
                {row.organizationLabel}
              </span>
              {row.hasBestPrice && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success-100 text-success-800">
                  {t('order_builder.table.best_price')}
                </span>
              )}
            </span>
          </NegotiationSellerCell>
        );
      case 'length':
        return (
          <TableCell key={key}>
            <span
              className={`text-sm font-semibold ${row.lengthLabel === 'Fixed' ? 'italic text-neut-500' : 'text-neut-900'}`}
            >
              {row.lengthLabel}
            </span>
            {row.inventoryOnly && (
              <span
                data-testid="inventory-length-badge"
                className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neut-100 text-neut-600"
              >
                {t('order_builder.table.inventory_length_badge')}
              </span>
            )}
          </TableCell>
        );
      case 'price':
        return (
          <TableCell
            key={key}
            bgColor={
              row.hasListedPrice
                ? row.isWithinSetPrice
                  ? 'success'
                  : 'error'
                : 'default'
            }
            className="relative"
          >
            <div className="text-sm">
              <div className="flex items-center gap-1">
                <span
                  data-testid="seller-price"
                  className={
                    row.hasListedPrice
                      ? `font-semibold ${row.isWithinSetPrice ? 'text-success-600' : 'text-error-600'}`
                      : 'font-semibold italic text-neut-sec'
                  }
                >
                  {row.hasListedPrice &&
                  row.isSingleValuePrice &&
                  row.rawPrice != null ? (
                    <AnimatedPrice
                      amount={row.rawPrice}
                      currencyCode={row.currencyCode}
                    />
                  ) : (
                    row.priceLabel
                  )}
                </span>
                {row.hasListedPrice && priceUnit && (
                  <span className="text-sm text-neut-600">
                    /{priceUnitLabel(priceUnit)}
                  </span>
                )}
                <PriceEditControl
                  isEditing={editingRowId === row.id}
                  isEdited={row.isPriceEdited}
                  defaultValue={
                    row.rawPrice == null ? null : roundUnitPrice(row.rawPrice)
                  }
                  onEditClick={() => handlePriceEditClick(row.id)}
                  onSubmit={(value) => handlePriceEditSubmit(value, row)}
                  onCancel={handlePriceEditCancel}
                  editTitle={t('order_builder.table.edit_price_title')}
                  editedTitle={t('order_builder.table.price_edited_title')}
                  editedAriaLabel={t(
                    'order_builder.table.price_edited_aria_label'
                  )}
                />
              </div>
            </div>
          </TableCell>
        );
      case 'leadTime':
        return (
          <TableCell key={key}>
            <div className="flex items-center text-sm">
              <Truck
                className="w-3.5 h-3.5 text-neut-400 mr-1"
                aria-hidden="true"
              />
              <span className="text-neut-900">{row.leadTimeLabel}</span>
            </div>
          </TableCell>
        );
      case 'actions': {
        const alreadyInCart = cartIdentifierSet.has(
          vendorGroupCartKey(row.platformProductLengthId, row.organizationId)
        );
        // The aggregate priceRange goes null on a currency mismatch even when every facility is
        // individually priced, so hasPricedFacility (blind to that mismatch) also opens the picker.
        const canAddToCart = row.hasListedPrice || row.hasPricedFacility;
        const accessibleLabel = alreadyInCart
          ? t('order_builder.table.already_in_cart_title')
          : canAddToCart
            ? t('order_builder.table.add_to_cart_title')
            : t('order_builder.table.no_listed_price_enter_price');
        return (
          <VendorGroupActionsCell
            key={key}
            row={row}
            facilityPickerBaseFilter={facilityPickerBaseFilter}
            selectedLocationId={selectedLocationId}
            alreadyInCart={alreadyInCart}
            accessibleLabel={accessibleLabel}
            onAddToCart={onAddToCart}
            onRemoveVendor={(organizationId, organizationName) =>
              setRemoveSellerTarget({ organizationId, organizationName })
            }
            t={t}
          />
        );
      }
      default:
        return (
          <TableCell key={key}>
            <span className="text-sm text-neut-sec">—</span>
          </TableCell>
        );
    }
  }

  return (
    <Fragment>
      {removeSellerTarget && (
        <RemoveSellerConfirmModal
          isOpen
          onClose={() => setRemoveSellerTarget(null)}
          organizationId={removeSellerTarget.organizationId}
          organizationName={removeSellerTarget.organizationName}
          platformProductId={platformProductId}
          platformProductLengthIds={platformProductLengthIds}
          onRemoved={onSellerRemoved}
        />
      )}
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        accessibility={getColumnDragAccessibility(
          t,
          (columnKey) =>
            activeHeaders.find((header) => header.key === columnKey)?.label ??
            columnKey
        )}
      >
        <div ref={scrollContainerRef} className="overflow-x-auto relative">
          <style>{`
        @keyframes sticky-col-glow {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.45; }
        }
        .sticky-col-glow {
          animation: sticky-col-glow 2s ease-in-out infinite;
        }
        ${PRICE_FLASH_STYLES}
      `}</style>
          <div
            ref={shadowRef}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: '16px',
              background:
                'linear-gradient(to left, rgba(0,0,0,0.12), transparent)',
              pointerEvents: 'none',
              zIndex: 30,
              opacity: 0,
            }}
          />

          <table className="w-full">
            <ComparisonTableHeadRow
              activeHeaders={
                isCannedTally
                  ? withClickSortable(activeHeaders, CANNED_TALLY_SORTABLE_KEYS)
                  : vendorHeaderDefinitions
              }
              unpinnedColumnKeys={unpinnedColumnKeys}
              activeSortKey={activeVendorSortKey}
              activeSortDirection={activeVendorSortDirection}
              onSort={onSort}
              onSortClick={handleVendorHeaderSortClick}
              leadingHeader={
                <TableHeader>
                  <span className="sr-only">
                    {t('order_builder.table.negotiate_price_title')}
                  </span>
                </TableHeader>
              }
            />
            <tbody
              data-testid="prices-and-availability-table-body"
              className="bg-neut-light divide-y divide-neut-border"
            >
              {isCannedTally
                ? (cannedTallyGroups ?? []).map((group) => (
                    <CannedTallySellerGroupRow
                      key={group.id}
                      group={group}
                      headers={activeHeaders}
                      platformProduct={platformProduct}
                      tallies={
                        talliesByOrgId.get(group.facility.organization.id) ?? []
                      }
                      selectedTallyName={selectedTallyName}
                      selectedLocationId={selectedLocationId}
                      cartIdentifiers={cartIdentifiers}
                      onAddTallyToCart={onAddTallyToCart ?? NOOP}
                      onTallyPriceEdit={onTallyPriceEdit ?? NOOP}
                      editedTallyPriceByKey={editedTallyPriceByKey}
                      onSuggestTallyPrice={onSuggestTallyPrice}
                      pendingTallyNegotiationKeys={pendingTallyNegotiationKeys}
                      negotiationSelection={negotiationSelection}
                      acceptedTallyPriceByKey={acceptedTallyPriceByKey}
                      editingRowId={editingRowId}
                      onEditClick={handlePriceEditClick}
                      onEditCancel={handlePriceEditCancel}
                      onRemoveVendor={(organizationId, organizationName) =>
                        setRemoveSellerTarget({
                          organizationId,
                          organizationName,
                        })
                      }
                      t={t}
                    />
                  ))
                : vendorGroupRows.map((row) => {
                    const negotiationState = negotiationCellState({
                      rowId: row.id,
                      target: row.negotiationTarget,
                      isPending: pendingNegotiationKeys.has(
                        row.negotiationTarget.negotiationKey
                      ),
                      selection: negotiationSelection,
                      t,
                    });
                    return (
                      <tr
                        key={row.id}
                        ref={(rowElement) => registerRow(row.id, rowElement)}
                        className="hover:bg-neut-50 transition-colors"
                        data-testid="vendor-group-row"
                      >
                        <NegotiationCheckboxCell
                          state={negotiationState}
                          sellerName={row.organizationLabel}
                        />
                        {activeHeaders.map((header) =>
                          renderVendorGroupCell(
                            row,
                            header.key,
                            negotiationState
                          )
                        )}
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
        {isCannedTally ? (
          <>
            {hasMoreCannedTallyGroups === false &&
              (cannedTallyGroups ?? []).length > 0 && (
                <div className="flex justify-center py-4">
                  <div className="text-sm text-neut-500">
                    {t('order_builder.table.no_more_sellers')}
                  </div>
                </div>
              )}
            {isLoadingMoreCannedTallyGroups &&
              (cannedTallyGroups ?? []).length > 0 && (
                <div className="flex justify-center py-4">
                  <div className="text-sm text-neut-500">
                    {t('order_builder.table.loading_more_sellers')}
                  </div>
                </div>
              )}
            <div
              ref={cannedTallyGroupsSentinelRef}
              style={{ height: '1px', background: 'transparent' }}
              data-testid="canned-tally-groups-scroll-sentinel"
            />
          </>
        ) : (
          <>
            {hasMoreVendorGroups === false && vendorGroupRows.length > 0 && (
              <div className="flex justify-center py-4">
                <div className="text-sm text-neut-500">
                  {t('order_builder.table.no_more_sellers')}
                </div>
              </div>
            )}
            {isLoadingMoreVendorGroups && vendorGroupRows.length > 0 && (
              <div className="flex justify-center py-4">
                <div className="text-sm text-neut-500">
                  {t('order_builder.table.loading_more_sellers')}
                </div>
              </div>
            )}
            <div
              ref={vendorGroupsSentinelRef}
              style={{ height: '1px', background: 'transparent' }}
              data-testid="vendor-groups-scroll-sentinel"
            />
          </>
        )}
        <DragOverlay dropAnimation={null}>
          {activeDragColumn ? (
            <div
              data-testid="column-drag-overlay"
              className="flex items-center gap-1 px-4 py-2 text-xs font-medium text-neut-500 uppercase tracking-wider bg-white rounded-md shadow-lg border border-neut-200"
            >
              <GripVertical size={14} className="text-neut-400" aria-hidden />
              {activeDragColumn.label}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Fragment>
  );
}
